////jc
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";

import { baseUrl, uploadFile, mediaUrl } from "../../../Utils/FileUpload";
import {
  ChooseFile,
  Submit,
  ContactNumber,
  Email,
  Address,
  EditVideoPackageTitle,
  PackageTitle,
} from "../../../Utils/Constants";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useNavigate, useLocation } from "react-router-dom";
import useCustomToasts from "../../../hooks/useCustomToast";

interface FormValues {
  name: string;
  image: FileList | null;
  amount: string;
  country_id: string;
  base_customer_credit_point: number;
  base_video_call_duration: number;
  base_agent_payment: number;
  base_agent_point: number;
  description: string;
}
interface ErrorResponse {
  message: string;
}
export default function EditVideoPackage() {
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const [loading, setLoading] = useState(true);
  const {
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      image: null,
      amount: "",
      country_id: "",
      description: "",
    },
  });
  const [countries, setCountries] = useState<{ id: number; name: string }[]>(
    []
  );
  const [baseValues, setBaseValues] = useState({
    base_amount: 0,
    base_customer_credit_point: 0,
    base_video_call_duration: 0,
    base_agent_point: 0,
    base_agent_payment: 0,
  });

  const [existingFile, setExistingFile] = useState<string>("");
  const location = useLocation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const amount = watch("amount");
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const token = localStorage.getItem("token");

        const [countryRes, packageSettingRes] = await Promise.all([
          axios.get(`${baseUrl}country`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${baseUrl}video_package_payment_setting/getall`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setCountries(countryRes.data);

        if (packageSettingRes.data && packageSettingRes.data.length > 0) {
          const baseData = packageSettingRes.data[0];
          setBaseValues({
            base_amount: parseFloat(baseData.base_amount),
            base_customer_credit_point: parseFloat(
              baseData.base_customer_credit_point
            ),
            base_video_call_duration: parseFloat(baseData.base_video_call_duration),
            base_agent_point: parseFloat(baseData.base_agent_point),
            base_agent_payment: parseFloat(baseData.base_agent_payment),
          });
        }
      } catch (error) {
        console.error("Failed to fetch countries or base values", error);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const inputAmount = parseFloat(String(amount || 0));
    const baseAmount = parseFloat(String(baseValues.base_amount));

    if (inputAmount && baseAmount) {
      setValue(
        "base_customer_credit_point",
        parseFloat(
          (
            (inputAmount / baseAmount) *
            baseValues.base_customer_credit_point
          ).toFixed(2)
        )
      );
      setValue(
        "base_video_call_duration",
        parseFloat(
          ((inputAmount / baseAmount) * baseValues.base_video_call_duration).toFixed(
            2
          )
        )
      );
      setValue(
        "base_agent_payment",
        parseFloat(
          ((inputAmount / baseAmount) * baseValues.base_agent_payment).toFixed(
            2
          )
        )
      );
      setValue(
        "base_agent_point",
        parseFloat(
          ((inputAmount / baseAmount) * baseValues.base_agent_point).toFixed(2)
        )
      );
    } else {
      setValue("base_customer_credit_point", 0);
      setValue("base_video_call_duration", 0);
      setValue("base_agent_payment", 0);
      setValue("base_agent_point", 0);
    }
  }, [amount, baseValues, setValue]);

  useEffect(() => {
    const fetchStaffsDetails = async () => {
      try {
        const token = localStorage.getItem("token");

        const result = await axios.post(
          `${baseUrl}video_package/viewbyid`,
          {
            id: location.state.id,
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setValue("name", result.data.name);
        setValue("image", result.data.image);
        setValue("country_id", result.data.country_id);

        setValue("description", result.data.description);

        // Helper to parse and remove unnecessary .00
        const cleanNumber = (value: string) => {
          const num = parseFloat(value);
          return Number.isInteger(num)
            ? parseInt(value).toString()
            : num.toString();
        };

        // Convert the number to a string before setting the value
        setValue("amount", cleanNumber(result.data.amount));
        setExistingFile(result.data.image);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching staffs details:", error);
      }
    };

    fetchStaffsDetails();
  }, [setValue]);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      let filename = existingFile;

      // Check if a new file is provided
      if (
        data.image &&
        data.image instanceof FileList &&
        data.image.length > 0
      ) {
        const file = data.image?.[0];
        filename = await uploadFile(file);
        setExistingFile(filename);
      }

      const editdata = {
        image: filename || existingFile,
        id: location.state.id,
        name: data.name,
        amount: data.amount,
        country_id: data.country_id,
        description: data.description,
      };
      // console.log(editdata, "editdata");
      const token = localStorage.getItem("token");
      const response = await axios.post(`${baseUrl}video_package/update`, editdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        SuccessToast("Data updated successfully", "/view_video_package");
      } else {
        ErrorToast("Failed to update");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorResponse = axiosError.response?.data as ErrorResponse;
      const errorMessage =
        errorResponse?.message || "An unexpected error occurred";
      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={EditVideoPackageTitle}
        parent={PackageTitle}
        title={EditVideoPackageTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xs={12}>
                  <Label htmlFor="package-name">Package Name</Label>
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      required: "Package name is required",
                      pattern: {
                        value: /^[A-Za-z0-9\s.'-]{2,50}$/,
                        message:
                          "Invalid package. Only letters,dots, numbers, spaces, hyphens (-), and apostrophes (') are allowed.",
                      },
                      validate: (value) => {
                        // Ensure it's not all numbers or just special characters

                        if (/^\d+$/.test(value)) {
                          return "Package cannot be all numbers.";
                        }
                        if (/^[^A-Za-z]+$/.test(value)) {
                          return "Package must include letters.";
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          id="package-name"
                          type="text"
                          placeholder="Package Name"
                          {...field}
                          invalid={!!errors.name}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.name ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.name && (
                          <FormFeedback>{errors.name.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="country">Country</Label>
                  <Controller
                    name="country_id"
                    control={control}
                    rules={{ required: "Country is required" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="select"
                          id="country"
                          {...field}
                          invalid={!!errors.country_id}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.country_id ? "red" : "",
                          }}
                          onKeyDown={handleKeyDown}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                              {country.name}
                            </option>
                          ))}
                        </Input>
                        {errors.country_id && (
                          <FormFeedback>
                            {errors.country_id.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="formFile">Image</Label>
                  <Controller
                    name="image"
                    control={control}
                    rules={{ required: "Image is required" }}
                    render={({ field: { onChange, onBlur, ref } }) => (
                      <>
                        <Input
                          id="formImage"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              onChange(e.target.files);
                              // Update the image preview immediately
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setExistingFile(reader.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          onBlur={onBlur}
                          innerRef={ref}
                          onKeyDown={handleKeyDown}
                        />
                        {existingFile && (
                          <img
                            src={
                              existingFile.startsWith("data:")
                                ? existingFile
                                : `${mediaUrl}/${existingFile}`
                            }
                            alt="Preview"
                            width="70"
                            height="70"
                            style={{ marginTop: "5px", borderRadius: "10px" }}
                          />
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="description">Description</Label>
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      required: "Description is required",
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          id="description"
                          type="textarea"
                          placeholder="Description"
                          {...field}
                          invalid={!!errors.description}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.description ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.description && (
                          <FormFeedback>
                            {errors.description.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Recharge Amount</Label>
                  <Controller
                    name="amount"
                    control={control}
                    rules={{ required: "Amount is required" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="number"
                          id="amount"
                          placeholder="Amount"
                          {...field}
                          invalid={!!errors.amount}
                          autoComplete="off"
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.amount ? "red" : "",
                          }}
                          onKeyDown={(e) => {
                            handleKeyDown(e);
                            handleAmountKeyDown(e);
                          }}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        />
                        {errors.amount && (
                          <FormFeedback>{errors.amount.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Customer Points</Label>
                  <Controller
                    name="base_customer_credit_point"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        step="0.01"
                        id="base_customer_credit_point"
                        placeholder="Customer Credit Point"
                        {...field}
                        disabled
                      />
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Video Call Duration (mins)</Label>
                  <Controller
                    name="base_video_call_duration"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        step="0.01"
                        id="base_video_call_duration"
                        placeholder="Call Duration"
                        {...field}
                        disabled
                      />
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Agent Points</Label>
                  <Controller
                    name="base_agent_point"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        step="0.01"
                        id="base_agent_point"
                        placeholder="Agent's Credit"
                        {...field}
                        disabled
                      />
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Agent Payment</Label>
                  <Controller
                    name="base_agent_payment"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        step="0.01"
                        id="base_agent_payment"
                        placeholder="Agent's Payment"
                        {...field}
                        disabled
                      />
                    )}
                  />
                </Col>

                <Col xs={12} className="mt-2">
                  <Btn color="primary" disabled={isButtonDisabled}>
                    {Submit}
                  </Btn>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
